import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, Layers, X } from 'lucide-react';
import { playCorrect, playWrong } from '../../utils/sounds';
import { PrecisionHUD, PrecisionStats, TrialStrip } from './PrecisionUI';
import { createNBackStream, getNBackRules, judgeNBack } from './memoryRules';
import { summarizeTrials } from './rules';
import type { PrecisionProps, PrecisionResult, TrialRecord } from './types';

export default function PrecisionNBack({ mode, tier, onComplete, onFail }: PrecisionProps) {
  const rules = useMemo(() => getNBackRules(mode, tier), [mode, tier]);
  const [stream] = useState(() => createNBackStream(rules.n, rules.rounds, rules.level));
  const [index, setIndex] = useState(0);
  const [stage, setStage] = useState<'show' | 'feedback' | 'gap'>('show');
  const [trials, setTrials] = useState<TrialRecord[]>([]);
  const [remaining, setRemaining] = useState(rules.windowMs);
  const [feedback, setFeedback] = useState('');
  const records = useRef<TrialRecord[]>([]);
  const kinds = useRef<string[]>([]);
  const locked = useRef(false);
  const finished = useRef(false);
  const started = useRef(0);
  const sessionStart = useRef(performance.now());
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const advanceTimer = useRef<ReturnType<typeof setTimeout>>();
  const resolveRef = useRef<(answer: boolean | null) => void>(() => {});
  const callbacks = useRef({ onComplete, onFail });
  callbacks.current = { onComplete, onFail };
  const warmup = index < rules.n;
  const stats = summarizeTrials(trials, rules.rounds);

  const advance = () => {
    setStage('gap');
    advanceTimer.current = setTimeout(() => { if (!finished.current) { setIndex(v => v + 1); setStage('show'); } }, rules.gapMs);
  };
  resolveRef.current = answer => {
    if (locked.current || finished.current || warmup || stage !== 'show') return;
    locked.current = true;
    const elapsed = performance.now() - started.current;
    const actual = answer === null || elapsed >= rules.windowMs ? null : answer;
    const judgment = judgeNBack(stream[index].match!, actual);
    kinds.current.push(judgment.kind);
    const outcome = judgment.outcome === 'perfect' && elapsed > rules.windowMs * 0.7 ? 'good' : judgment.outcome;
    const next: TrialRecord[] = [...records.current, { index: index - rules.n, outcome, responseMs: actual === null ? null : Math.round(elapsed), label: judgment.label }];
    records.current = next; setTrials(next);
    setFeedback(`${judgment.label} · 本项与前 ${rules.n} 项${stream[index].match ? '相同' : '不同'}`);
    setStage('feedback');
    judgment.outcome === 'perfect' ? playCorrect() : playWrong();
    const summary = summarizeTrials(next, rules.rounds);
    const failed = summary.mistakes >= rules.mistakeLimit;
    timer.current = setTimeout(() => {
      if (finished.current) return;
      if (failed || next.length >= rules.rounds) {
        finished.current = true;
        const count = (kind: string) => kinds.current.filter(k => k === kind).length;
        const precision: PrecisionResult = {
          ...summary, version: 1, tier: rules.level, trials: next, durationSeconds: (performance.now() - sessionStart.current) / 1000,
          failureReason: failed ? `失误达到 ${rules.mistakeLimit} 次。请只比较前 ${rules.n} 项，不要把上一项当作答案；可先回到标准的一步回看练习。` : undefined,
          responseLabel: '平均正确判断用时',
          metrics: [{ label: '正确命中', value: `${count('hit')} 次` }, { label: '误报相同', value: `${count('falseAlarm')} 次` }, { label: '漏判相同', value: `${count('miss')} 次` }, { label: '正确排除', value: `${count('reject')} 次` }, { label: '未作答', value: `${count('timeout')} 次` }, { label: '回看距离', value: `前 ${rules.n} 项` }],
          insights: [count('falseAlarm') > count('miss') ? '误报偏多：相邻重复不一定是目标，注意回看距离。' : count('miss') ? '漏判偏多：每出现一项就在心中更新位置队列，及时移出最早一项。' : '持续更新记忆队列，再确认相同或不同，保持稳定判断。', '相同与不同题目各占一半；预热只记忆、不计分。'],
        };
        const replay = { mechanic: 'nback', precision };
        failed ? callbacks.current.onFail(replay) : callbacks.current.onComplete(summary.score, replay);
      } else advance();
    }, 850);
  };

  useLayoutEffect(() => {
    finished.current = false; locked.current = false; started.current = performance.now();
    setRemaining(rules.windowMs);
    const deadlineTimer = setTimeout(() => {
      if (warmup) { locked.current = true; advance(); }
      else resolveRef.current(null);
    }, rules.windowMs);
    const interval = setInterval(() => { if (!locked.current) setRemaining(Math.max(0, rules.windowMs - (performance.now() - started.current))); }, 50);
    return () => { clearTimeout(deadlineTimer); clearInterval(interval); };
  }, [index, rules, warmup]);
  useEffect(() => { finished.current = false; return () => { finished.current = true; clearTimeout(timer.current); clearTimeout(advanceTimer.current); }; }, []);
  useEffect(() => {
    const keydown = (e: KeyboardEvent) => {
      if (e.repeat || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      if ((e.target as HTMLElement).matches('button,input')) return;
      e.preventDefault(); resolveRef.current(e.key === 'ArrowLeft');
    };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  }, []);

  return <div className="precision-game nback-game">
    <PrecisionHUD round={warmup ? 0 : index - rules.n + 1} total={rules.rounds} combo={stats.combo} mistakes={stats.mistakes} limit={rules.mistakeLimit} progress={remaining / rules.windowMs} label={warmup ? '记忆预热 · 不计分' : '工作记忆更新'} />
    <div className="precision-rule"><span><Layers size={14} className="inline mr-1" />回看前 {rules.n} 项</span><strong>{warmup ? `先记住第 ${index + 1} 个位置` : '与前面的目标位置相同吗？'}</strong><small>{warmup ? `前 ${rules.n} 项只观察，不用作答` : `只比较前 ${rules.n} 项，不是${rules.n > 1 ? '上一项' : '所有已出现的位置'} · ${(remaining / 1000).toFixed(1)} 秒`}</small></div>
    <div className="nback-lag" aria-label={`对照前 ${rules.n} 项`}>{Array.from({ length: rules.n + 1 }, (_, i) => <span key={i} className={i === 0 ? 'target' : i === rules.n ? 'current' : ''}>{i === 0 ? `前 ${rules.n} 项` : i === rules.n ? '当前' : `前 ${rules.n - i} 项`}</span>)}</div>
    <div className="precision-grid nback-board" style={{ gridTemplateColumns: 'repeat(3,1fr)' }} aria-label="工作记忆位置阵列">
      {Array.from({ length: 9 }, (_, cell) => <div key={cell} className={`precision-cell memory-cell ${stage !== 'gap' && stream[index].cell === cell ? 'lit' : ''}`} aria-label={`位置 ${cell + 1}${stage !== 'gap' && stream[index].cell === cell ? ' 当前亮起' : ''}`}><span className="memory-position">{cell + 1}</span>{stage !== 'gap' && stream[index].cell === cell && <span className="nback-dot" />}</div>)}
    </div>
    <div className="nback-actions"><button disabled={warmup || stage !== 'show'} onClick={() => resolveRef.current(true)}><Check size={17} /><strong>相同</strong><small>左方向键</small></button><button disabled={warmup || stage !== 'show'} onClick={() => resolveRef.current(false)}><X size={17} /><strong>不同</strong><small>右方向键</small></button></div>
    <p className="precision-feedback" role="status">{stage === 'feedback' ? feedback : stage === 'gap' ? '更新记忆，下一项即将出现' : warmup ? '记住位置，等待判断开始' : '每项只能判断一次，超时也会计为失误'}</p>
    <TrialStrip trials={trials} total={rules.rounds} />
    <PrecisionStats stats={[{ label: '本局评分', value: stats.score }, { label: '正确判断', value: trials.length ? `${stats.accuracy}%` : '—' }, { label: '最高连击', value: stats.maxCombo }]} />
    <p className="precision-note">高档会出现相邻重复干扰。正确判断且用时不超过时限的 70% 记为精准；错选或未作答都会中断连击。</p>
  </div>;
}
